import { useId, type ReactNode, type InputHTMLAttributes, type SelectHTMLAttributes } from 'react';

interface FieldProps { label: string; hint?: string; error?: string | null; children: (ids: { id: string; describedBy: string | undefined; invalid: boolean }) => ReactNode }

/** Label + control + hint + error wired with ids (WCAG 1.3.1, 3.3.1, 3.3.2). */
export function Field({ label, hint, error, children }: FieldProps) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errId = error ? `${id}-err` : undefined;
  const describedBy = [hintId, errId].filter(Boolean).join(' ') || undefined;
  return (
    <div className="qm-field" data-invalid={error ? 'true' : undefined}>
      <label className="qm-field__label" htmlFor={id}>{label}</label>
      {children({ id, describedBy, invalid: !!error })}
      {hint ? <p className="qm-field__hint" id={hintId}>{hint}</p> : null}
      {error ? <p className="qm-field__error" id={errId} role="alert">{error}</p> : null}
    </div>
  );
}

export function TextInput(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={['qm-input', props.className].filter(Boolean).join(' ')} />;
}

export function Select(props: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props} className={['qm-input', props.className].filter(Boolean).join(' ')} />;
}

/** On/off switch with role="switch" + aria-checked. */
export function Toggle({ checked, onChange, label, disabled, id }: { checked: boolean; onChange: (v: boolean) => void; label: string; disabled?: boolean; id?: string }) {
  return (
    <button id={id} type="button" role="switch" aria-checked={checked} aria-label={label} disabled={disabled}
      className="qm-toggle" data-checked={checked ? 'true' : 'false'} onClick={() => onChange(!checked)}>
      <span className="qm-toggle__knob" aria-hidden="true" />
    </button>
  );
}
